/**
 * The panel packer over an ARRAY of placeable regions (SD-4.2; grown in SD-4.5).
 *
 * SD-4.2 ships the pure core: grid-fill each region with module-sized rects, rejecting anything
 * that leaves the region or touches a keep-out. SD-4.5 adds best-first plane ordering, the
 * target-size control and orientation choice on top of this function, not instead of it.
 *
 * A single-region signature would be a latent bug — `offsetPolygonPerEdge` legitimately returns
 * several regions — so the array is the type, and `packPanels([])` is a valid question with a
 * valid answer: no panels.
 *
 * ── SD-4.6: TWO GEOMETRIES, AND KEEPING THEM APART IS THE POINT ────────────────────────────────
 * The keep-out subtraction runs through `booleans.subtractRegions` (`polygonClipping.difference`,
 * which SD-4.6's AC names), producing the PLACEABLE area a panel must fit inside. The GRID ANCHOR
 * stays the un-subtracted region.
 *
 * That separation is load-bearing rather than tidy. The grid origin is derived from the region's
 * bounding box, so if the subtracted geometry anchored it, drawing a keep-out that clipped an edge
 * would shift the origin and RENUMBER every cell on the face — and SD-4.1 persists a rep's panel
 * toggles by exactly those numbers. A keep-out would silently reassign every decision on the face.
 *
 * `difference` also legitimately returns SEVERAL polygons for one input region (a keep-out cutting a
 * face in two). Those stay ONE `regionIndex` — the caller's — for the same reason: the region index
 * is part of the decision key.
 *
 * The behavioural outcome is identical to SD-4.2's reject-on-intersection policy, because
 * `rect ⊆ region ∧ rect ∩ keepOuts = ∅` and `rect ⊆ (region − keepOuts)` are the same set. What the
 * routing buys is that the placeable area is now a VALUE — drawable, composable with SD-4.3's
 * envelope — rather than an implicit condition inside the packing loop.
 */

import { subtractRegions } from './booleans';
import { rectInRegion, rectIntersectsRegion } from './predicates';
import type { Pt } from './types';

export interface PanelRect {
  /** Lower-left corner, metres. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Index into the `regions` argument this panel was placed in. */
  regionIndex: number;
  /**
   * Grid indices within the region (SD-4.5): column and row of the packing grid this rect
   * occupies. SD-4.1 persists a toggle by (plane, region, grid i, grid j), so these must be
   * STABLE under recompute: they derive from the grid origin (the region's bounding-box min),
   * not from how many panels happened to fit before this one.
   */
  i: number;
  j: number;
}

export interface PackOptions {
  /** Module footprint on the roof plane, metres. */
  panelW: number;
  panelH: number;
  /** Clearance between panels, metres. Defaults to 0.02 (the spike's figure). */
  gap?: number;
  /**
   * Keep-out polygons in the same coordinate frame. May be self-intersecting (a bowtie drawn by
   * a user is legal input); they are resolved through the same union that resolves offset
   * self-intersection before any panel is tested.
   */
  keepOuts?: readonly (readonly Pt[])[];
  /**
   * Where the packing grid anchors (SD-4.5). 'bbox' (default, the SD-4.2 behaviour) starts at
   * the region's bounding-box min; 'centered' distributes the remainder evenly on both sides,
   * which recovers the rows a corner-anchored grid pushes off an irregular face — Oak Park
   * packs 17 corner-anchored and far more centered. Both are geometry-derived, so grid
   * indices stay stable under recompute either way.
   */
  gridOrigin?: 'bbox' | 'centered';
}

/**
 * Degenerate geometry is legal, not an error: an empty region list, a region smaller than one
 * module, and a keep-out covering everything all return zero panels; a keep-out entirely outside
 * every region changes nothing.
 */
export function packPanels(regions: readonly (readonly Pt[])[], opts: PackOptions): PanelRect[] {
  const { panelW, panelH } = opts;
  if (!Number.isFinite(panelW) || panelW <= 0 || !Number.isFinite(panelH) || panelH <= 0) {
    throw new Error(`packPanels: module must have positive dimensions, got ${panelW} × ${panelH}`);
  }
  const gap = opts.gap ?? 0.02;
  const keepOuts = opts.keepOuts ?? [];

  const panels: PanelRect[] = [];
  regions.forEach((region, regionIndex) => {
    if (!region || region.length < 3) return;

    // The placeable area for THIS region. `subtractRegions` normalises each keep-out through the
    // union first, so a user-drawn bowtie has both lobes subtracted. Several pieces here are one
    // `regionIndex`; a rect is placeable if it fits inside any one of them.
    const placeable = keepOuts.length
      ? subtractRegions([region], keepOuts)
      : [{ outer: [...region] as Pt[], holes: [] }];
    if (placeable.length === 0) return;

    const fitsPlaceable = (x: number, y: number): boolean =>
      placeable.some(
        (area) =>
          rectInRegion(x, y, panelW, panelH, area.outer) &&
          !area.holes.some((hole) => rectIntersectsRegion(x, y, panelW, panelH, hole))
      );
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of region) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    let originX = minX;
    let originY = minY;
    if (opts.gridOrigin === 'centered') {
      const fit = (extent: number, panel: number): number =>
        Math.max(0, Math.floor((extent + gap) / (panel + gap)));
      const remainder = (extent: number, panel: number): number => {
        const n = fit(extent, panel);
        return n === 0 ? 0 : extent - (n * panel + (n - 1) * gap);
      };
      originX = minX + remainder(maxX - minX, panelW) / 2;
      originY = minY + remainder(maxY - minY, panelH) / 2;
    }
    for (let j = 0, y = originY; y + panelH <= maxY; j += 1, y = originY + (j * (panelH + gap))) {
      for (let i = 0, x = originX; x + panelW <= maxX; i += 1, x = originX + (i * (panelW + gap))) {
        if (!fitsPlaceable(x, y)) continue;
        panels.push({ x, y, w: panelW, h: panelH, regionIndex, i, j });
      }
    }
  });
  return panels;
}
